import { useMemo } from 'react'
import { gql, useQuery } from '@apollo/client'
import { getCookie, setCookie } from 'cookies-next'
import { REGION_COOKIE } from '@/constants/cookies'
import { Durations } from '@/constants/durations'
import { CANADA, DEFAULT_REGION, UNITED_STATES } from '@/constants/regions'
import { GetViewerRegionQuery } from '@/types/codegen-federation'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { isDevelopmentEnvironment } from '@/utils/environment-utils'
import { Maybe } from '@/utils/types'

const getViewerRegionQuery = gql`
  query getViewerRegion {
    viewer {
      region
    }
  }
`

interface UseUserRegionResult {
  loading: boolean
  region: string
}

export function useUserRegion(): UseUserRegionResult {
  const result = useQuery<GetViewerRegionQuery>(getViewerRegionQuery, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  })

  return useMemo(() => {
    const hydraRegion = result?.data?.viewer?.region
    const cookieRegion = getRegionFromCookie()
    const region = overrideOrDefaultRegion(hydraRegion || cookieRegion)

    if (hydraRegion) {
      setCookie(REGION_COOKIE, hydraRegion, { maxAge: Durations.ONE_MONTH_IN_SECONDS })
    }

    return { loading: result.loading, region }
  }, [result])
}

export function getUserRegionFromReq(req: { headers: Record<string, string | string[] | undefined> }): string {
  const vercelRegion = getItemOrFirstEntry(req.headers['x-vercel-ip-country'])
  return overrideOrDefaultRegion(vercelRegion)
}

// TODO: Providing a default region may be a terrible idea.
// We are seeing errors where users are getting the default region, US, when they are elsewhere in the world.
// I believe we should return undefined if we don't have a value.
// All usages would need to be updated to handle this.
export function overrideOrDefaultRegion(region: Maybe<string>): string {
  return overrideOrRegion(region) || DEFAULT_REGION
}

export function overrideOrRegion(region: Maybe<string>): string | undefined {
  const mockRegion = process.env.NEXT_PUBLIC_MOCK_REGION?.toUpperCase()
  if (isDevelopmentEnvironment() && mockRegion) return mockRegion
  return region?.toUpperCase()
}

export function isUnitedStatesOrCanada(region: Maybe<string>): boolean {
  if (!region) return false
  const upper = region.toUpperCase()
  return Boolean(upper === UNITED_STATES || upper === CANADA)
}

export function isUnitedStates(region: Maybe<string>): boolean {
  if (!region) return false
  const upper = region.toUpperCase()
  return Boolean(upper === UNITED_STATES)
}

/**
 * NOTE - Only use this function client side. If used server side, the cookie will always be one request behind.
 * Use getUserRegionFromReq server side.
 * @returns
 */
export function getRegionFromCookie(): string | undefined {
  const region = getCookie(REGION_COOKIE)
  return typeof region === 'string' ? region : undefined
}
